import type {
	GetWorkflowDetailsResponseJSON,
	ListWorkflowDetailTodosResponseJSON,
	ListWorkflowTodosResponseJSON,
	ListWorkflowsResponseJSON,
	UpdateWorkflowArchivedResponseJSON,
	WorkflowDetailTodoStatusFilter,
	WorkflowSortBy,
	WorkflowStatusFilter,
	WorkflowTodoSortBy,
	WorkflowTodoStatusFilter,
} from "@performyard/service-contracts";

import legacyRequest from "../base-request";
import request, { ErrorResponse, internalStreamRequest } from "./internal-api/internal-base-request";

export type {
	WorkflowDetailTodoStatusFilter,
	GetWorkflowDetailsResponseJSON,
	WorkflowSortBy,
	WorkflowStatusFilter,
	WorkflowTodoSortBy,
	WorkflowTodoStatusFilter,
};

/**
 * Serializes filter/sort params for the workflows endpoints.
 *
 * Skips `undefined`, `""` and `false` so an unset filter is absent rather than sent
 * as an empty string the API would treat as a real value. Params where `false` is
 * meaningful (`hasSignatureRequest`) must therefore be set explicitly by the caller.
 */
const toQuery = (params: Record<string, string | number | boolean | undefined>): string => {
	const searchParams = new URLSearchParams();
	for (const [key, value] of Object.entries(params)) {
		if (value === undefined || value === "" || value === false) {
			continue;
		}
		searchParams.set(key, String(value));
	}
	return searchParams.toString();
};

const withQuery = (path: string, query: string): string => `${path}${query ? `?${query}` : ""}`;

export type ListWorkflowsParams = {
	sortBy?: WorkflowSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
	name?: string;
	workflowName?: string;
	subject?: string;
	status?: WorkflowStatusFilter;
	overdueOnly?: boolean;
	unassignedOnly?: boolean;
};

export const getWorkflows = async (params: ListWorkflowsParams = {}): Promise<ListWorkflowsResponseJSON> => {
	const [response] = await request<ListWorkflowsResponseJSON>(withQuery("/v1/workflows", toQuery(params)), {
		method: "GET",
	});
	return response;
};

export type WorkflowTodoFilterParams = {
	action?: string;
	subject?: string;
	actor?: string;
	workflow?: string;
	status?: WorkflowTodoStatusFilter;
	dueDateStart?: string;
	dueDateEnd?: string;
	completedDateStart?: string;
	completedDateEnd?: string;
};

export type ListWorkflowTodosParams = WorkflowTodoFilterParams & {
	sortBy?: WorkflowTodoSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

export const getWorkflowTodos = async (
	params: ListWorkflowTodosParams = {}
): Promise<ListWorkflowTodosResponseJSON> => {
	const [response] = await request<ListWorkflowTodosResponseJSON>(withQuery("/v1/workflows/todos", toQuery(params)), {
		method: "GET",
	});
	return response;
};

export const downloadWorkflowTodosCsv = async (params: WorkflowTodoFilterParams = {}): Promise<Blob> => {
	const response = await internalStreamRequest(withQuery("/v1/workflows/todos/csv", toQuery(params)), {
		method: "GET",
	});
	if (!response.ok) {
		throw new ErrorResponse(response.statusText || "Bad fetch response", response);
	}
	return response.blob();
};

export type LaunchWorkflowTodoJSON =
	| {
			label: string;
			daysToComplete: number;
			actor: string;
	  }
	| {
			label: string;
			daysToComplete: number;
			signatureRequestTemplateID: string;
			signers: string[];
	  };

export type LaunchWorkflowPayload = {
	name: string;
	templateID: string;
	startDate: string;
	userSelectionID: string;
	defaultManagerID: string | null;
	todos: LaunchWorkflowTodoJSON[];
};

export type LaunchWorkflowResponseJSON = {
	jobID: string;
};

export const launchWorkflow = async (payload: LaunchWorkflowPayload): Promise<LaunchWorkflowResponseJSON> => {
	const [response] = await legacyRequest<LaunchWorkflowResponseJSON>("/api/launch-workflow/", {
		method: "POST",
		body: JSON.stringify({ workflow: payload }),
	});
	return response;
};

export type WorkflowLaunchJobJSON = {
	_id: string;
	started?: boolean;
	finished?: boolean;
	progress_count?: number;
	progress_total?: number;
};

export const getWorkflowLaunchJob = async (jobId: string): Promise<WorkflowLaunchJobJSON> => {
	const [response] = await legacyRequest<WorkflowLaunchJobJSON>(`/api/user_jobs/${jobId}/`);
	return response;
};

export const getWorkflowDetails = async (workflowId: string): Promise<GetWorkflowDetailsResponseJSON> => {
	const [response] = await request<GetWorkflowDetailsResponseJSON>(`/v1/workflows/${workflowId}`, { method: "GET" });
	return response;
};

export type WorkflowDetailTodoFilterParams = {
	action?: string;
	actor?: string;
	status?: WorkflowDetailTodoStatusFilter;
	dueDateStart?: string;
	dueDateEnd?: string;
	hasSignatureRequest?: boolean;
};

export type ListWorkflowDetailTodosParams = WorkflowDetailTodoFilterParams & {
	sortBy?: WorkflowTodoSortBy;
	sortOrder?: "asc" | "desc";
	limit?: number;
	skip?: number;
};

export const getWorkflowDetailTodos = async (
	workflowId: string,
	params: ListWorkflowDetailTodosParams = {}
): Promise<ListWorkflowDetailTodosResponseJSON> => {
	// hasSignatureRequest is stringified up front because `false` is a real filter
	// value here ("to-dos without a signature request"), and toQuery drops `false`.
	const query = toQuery({
		...params,
		hasSignatureRequest: params.hasSignatureRequest === undefined ? undefined : String(params.hasSignatureRequest),
	});

	const [response] = await request<ListWorkflowDetailTodosResponseJSON>(
		withQuery(`/v1/workflows/${workflowId}/todos`, query),
		{ method: "GET" }
	);
	return response;
};

export const updateWorkflowArchived = async (
	workflowId: string,
	archived: boolean
): Promise<UpdateWorkflowArchivedResponseJSON> => {
	const [response] = await request<UpdateWorkflowArchivedResponseJSON>(`/v1/workflows/${workflowId}`, {
		method: "PATCH",
		body: JSON.stringify({ archived }),
	});
	return response;
};

export const describeLaunchWorkflowError = (error: unknown): string => {
	const errors = (error as { data?: { errors?: unknown } } | null)?.data?.errors;
	if (errors === "Invalid actor") {
		return "An assignee on one of the to-dos has been deactivated. Assign another employee and try again.";
	}
	return "An error occurred while launching the workflow";
};

// --- Legacy Tornado endpoints -------------------------------------------------
// To-do mutations still go through the battle-tested /ajax/ handlers so their side
// effects (reminder notification scheduling, signature-request cascade deletes,
// audit logging) stay intact. They read form-encoded args, respond with an
// `errors` array, and — except for check — report validation failures with a 200.
//
// AUTHORIZATION: none of these handlers consults rbac.json, and they do not all
// enforce the same rule, so `workflows:manageAdmin` — which the details page uses to
// decide whether to show the write UI — is never what actually authorizes them:
//
//   - /ajax/add-workflow-todo/ and /ajax/remove-workflow-todo/ raise 403 unless
//     has_basic_admin_or_admin_access (company.admins + company.basic_admins), with
//     no overlord branch.
//   - /ajax/check-workflow-todo/ and /ajax/cancel-workflow/ have no role gate at all.
//     They narrow the Mongo selector instead — an overlord skips the company scope
//     entirely, and a non-admin is restricted to rows where actor == user._id — so a
//     caller who fails the check gets a 404, not a 403.
//
// manageAdmin is aligned to the add/remove pair because those are the binding
// constraint: it is granted to exactly admin + basic-admin (see rbac.json
// "workflows"), and an overlord is deliberately excluded so the page never offers
// writes that would 403 there. Revisit if these move to internal-api — the check and
// cancel handlers are *looser* than the UI gate, so a future "assignees may tick
// their own to-dos" change must not assume manageAdmin is the server-side rule.

export class WorkflowActionError extends Error {}

/**
 * Message to show a user for a failed legacy to-do mutation.
 *
 * Only `WorkflowActionError` carries a message the legacy handler meant for a
 * human. Anything else is transport-level — base-request's `ErrorResponse`
 * ("Bad fetch response"), or a `SyntaxError` from parsing a Tornado HTML error
 * page as JSON — so it must not be shown verbatim.
 */
export const describeWorkflowActionError = (error: unknown, fallback: string): string =>
	error instanceof WorkflowActionError && error.message ? error.message : fallback;

const legacyFormPost = async <T extends { errors?: string[] }>(
	url: string,
	fields: Record<string, string>
): Promise<T> => {
	let data: T;
	try {
		const [response] = await legacyRequest<T>(url, {
			method: "POST",
			headers: { "Content-Type": "application/x-www-form-urlencoded" },
			body: new URLSearchParams(fields).toString(),
		});
		data = response;
	} catch (error) {
		// The check handler responds 400 (with an errors payload) for guarded todos,
		// e.g. marking a signature to-do complete. Surface that message.
		const errors = (error as { data?: { errors?: string[] } })?.data?.errors;
		if (errors?.length) {
			throw new WorkflowActionError(errors.join(" "));
		}
		throw error;
	}
	if (data.errors?.length) {
		throw new WorkflowActionError(data.errors.join(" "));
	}
	return data;
};

export const checkWorkflowTodo = async ({ todoId, done }: { todoId: string; done: boolean }): Promise<void> => {
	await legacyFormPost("/ajax/check-workflow-todo/", {
		todoid: todoId,
		action: done ? "check" : "uncheck",
	});
};

export type SaveWorkflowTodoParams = {
	workflowId: string;
	/** When present the legacy handler updates the (not-yet-done) todo instead of creating one. */
	todoId?: string;
	name: string;
	actorId: string;
	/** Calendar date, YYYY-MM-DD. */
	dueDate: string;
};

export const saveWorkflowTodo = async ({
	workflowId,
	todoId,
	name,
	actorId,
	dueDate,
}: SaveWorkflowTodoParams): Promise<void> => {
	await legacyFormPost("/ajax/add-workflow-todo/", {
		workflow_id: workflowId,
		...(todoId ? { todoid: todoId } : {}),
		action: name,
		actor: actorId,
		due_date: dueDate,
	});
};

export const removeWorkflowTodo = async ({ todoId }: { todoId: string }): Promise<void> => {
	await legacyFormPost("/ajax/remove-workflow-todo/", { todoid: todoId });
};

export const cancelWorkflow = async ({ workflowId }: { workflowId: string }): Promise<void> => {
	await legacyFormPost("/ajax/cancel-workflow/", { workflow_id: workflowId });
};
